import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useWebSettings } from '../../../context/WebSettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { formatPercentage } from '../../../i18n/FormatCurrency';


interface CategoryData {
    id: number;
    category_name: string;
    category_icon: string;
    discount_percent: string;
    description: string;
}

interface CategoryModalProps {
  setShowAllModal: React.Dispatch<React.SetStateAction<boolean>>;
  categories: CategoryData[];
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
    setShowAllModal,
    categories,
    selectedCategory,
    setSelectedCategory
}) => {
    const { webData } = useWebSettings();
    const isImage = webData?.include_image === 1;
    const { t } = useTranslation();
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setShowAllModal(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-stone-800 rounded-xl p-6 m-4 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-black dark:text-zinc-100">
              {t('All Categories')}
            </h2>
            <button
              onClick={() => setShowAllModal(false)}
              className="text-black dark:text-zinc-100 text-xl hover:text-gray-600 dark:hover:text-gray-400"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                whileHover={{ scale: 1.02 }}
                className={clsx(
                  'flex items-center gap-2 px-4 py-3 rounded-xl shadow-md cursor-pointer transition-colors duration-300 ease-in-out relative',
                  selectedCategory === category.id.toString()
                    ? 'bg-orange-400 shadow-gray-500/40'
                    : 'bg-white dark:bg-stone-700 hover:bg-gray-100 hover:dark:bg-stone-600'
                )}
                onClick={() => {
                  setSelectedCategory(category.id.toString());
                  setShowAllModal(false);
                }}
              >
                {isImage && (
                  <img
                    src={category.category_icon || 'defaultIconUrl.png'}
                    alt={`${category.category_name} category icon`}
                    className="w-7 h-7 bg-stone-300 dark:bg-stone-500 p-0.5 rounded-full flex-shrink-0"
                  />
                )}
                <h3 className="text-black dark:text-zinc-100 font-bold truncate">
                  {category.category_name}
                </h3>
                {parseFloat(category.discount_percent) > 0 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {formatPercentage({value: category.discount_percent})}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CategoryModal;
